import { useEffect, useMemo } from 'react';

import {
  AadhaarCardMock,
  PreviewHeader,
} from '@/features/workflows/components/simple/preview-screens';
import { cn } from '@/lib/utils';

import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { formatResultDateTime } from '../../sessionDateTime';
import { formatGender } from '../../sessionFieldFormat';
import { aadhaarScreenKind, extractAadhaarError } from './aadhaar.utils';

const METHOD_LABEL: Record<string, string> = {
  'digilocker-pinless': 'DigiLocker (without PIN)',
  'digilocker-pin': 'DigiLocker (with PIN)',
  'secure-qr': 'Aadhaar Secure QR',
  ovse: 'Aadhaar App (OVSE)',
  ocr: 'Aadhaar OCR',
};

const STATUS_STYLE: Record<string, string> = {
  Passed: 'bg-[#DCFCE7] text-[#166534]',
  Review: 'bg-[#FEF9C3] text-[#854D0E]',
  Failed: 'bg-[#FEE2E2] text-[#991B1B]',
  Pending: 'bg-[#F3F4F6] text-[#374151]',
};

// Read-only Aadhaar step detail for the view / review screens. Aadhaar steps
// complete via the live runner (DigiLocker popup / OCR) + webhook, so here we
// only present what was captured — no re-run form. Reports not-ready so the
// review panel's verify button stays disabled for this inspection view.
export function AadhaarDetail({
  step,
  formId,
  onPendingChange,
  onReadyChange,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
  formId: string;
  onPendingChange: (pending: boolean) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  useEffect(() => {
    onPendingChange(false);
    onReadyChange(false);
  }, [onPendingChange, onReadyChange]);

  const kind = aadhaarScreenKind(step.apiBindings[0] ?? { operationId: '', path: '' });
  const method = METHOD_LABEL[kind] ?? 'Aadhaar Verification';
  const error = extractAadhaarError(step.resultJson);

  // Pull a few human-readable claims out of the stored result, when present.
  const claims = useMemo(() => extractClaims(step.resultJson), [step.resultJson]);

  return (
    <form id={formId} onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-4">
      <PreviewHeader title="Aadhaar Verification" subtitle={method} />

      <div className="w-full">
        <AadhaarCardMock large />
      </div>

      <div className="flex items-center justify-between rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
        <span className="text-sm text-[#6B7280]">Status</span>
        <span
          className={cn(
            'rounded-full px-2.5 py-0.5 text-xs font-semibold',
            STATUS_STYLE[step.status] ?? STATUS_STYLE.Pending,
          )}
        >
          {step.status}
        </span>
      </div>

      {claims.length > 0 && (
        <div className="flex flex-col divide-y divide-[#F3F4F6] rounded-lg border border-[#E5E7EB]">
          {claims.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm text-[#6B7280]">{label}</span>
              <span className="text-sm font-medium text-[#111827]">{value}</span>
            </div>
          ))}
        </div>
      )}

      {step.status === 'Pending' && (
        <p className="text-xs leading-snug text-[#9CA3AF]">
          This Aadhaar step is completed by the applicant in the live session (DigiLocker / Aadhaar
          App) and finalised by the provider webhook.
        </p>
      )}

      {error && step.status === 'Failed' && <p className="text-xs text-[#BF3636]">{error}</p>}
    </form>
  );
}

// Canonical Aadhaar Return-Data fields (PDF §5.5), in display order. The stored
// result is the NORMALISED, filtered object (see AadhaarResultNormalizer +
// ApplyReturnFieldFilter on the backend), so we render exactly the keys present —
// i.e. the fields the admin left enabled in Return Data. Values are already mapped
// to these keys regardless of which API ran (Secure QR / OCR / DigiLocker).
const AADHAAR_FIELD_LABELS: Array<{ key: string; label: string }> = [
  { key: 'reference_id', label: 'Aadhaar Reference ID' },
  { key: 'name', label: 'Name on Aadhaar' },
  { key: 'dob', label: 'Date of Birth' },
  { key: 'gender', label: 'Gender' },
  { key: 'address', label: 'Address' },
  { key: 'pincode', label: 'PIN Code' },
  { key: 'state', label: 'State' },
  { key: 'masked_mobile', label: 'Mobile No.' },
  { key: 'masked_email', label: 'Email Id' },
  { key: 'name_match_score', label: 'Name Match Score vs PAN' },
  { key: 'verified_at', label: 'Verified At' },
  { key: 'method_used', label: 'Method Used' },
  { key: 'consent_captured_at', label: 'Consent Timestamp' },
];

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (key === 'name_match_score') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : `${n.toFixed(0)}%`;
  }
  if (key === 'gender') return formatGender(value);
  if (key === 'verified_at' || key === 'consent_captured_at') {
    return formatResultDateTime(value);
  }
  return String(value);
}

// Reads the canonical Aadhaar keys present in the stored result, in display order.
function extractClaims(resultJson: string | null): [string, string][] {
  if (!resultJson) return [];
  let parsed: Record<string, unknown> | null = null;
  try {
    const p = JSON.parse(resultJson);
    parsed = p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
  } catch {
    return [];
  }
  if (!parsed) return [];
  const out: [string, string][] = [];
  for (const { key, label } of AADHAAR_FIELD_LABELS) {
    if (key in parsed && parsed[key] !== null && parsed[key] !== '') {
      out.push([label, formatValue(key, parsed[key])]);
    }
  }
  return out;
}
